import React from "react";

import { Alert, useMediaQuery, useTheme } from "@mui/material";
import { CustomAnimation } from "@thekilroygroup/shared-ui";
import { fadeInAccordionAnimations } from "pages/CheckoutPage/animations.checkOutPage";
import { YouthStudentInfo } from "components/InfoTextBox/YouthStudentInfoTextBox";
import AdditionalProducts from "../../AdditionalProducts/AdditionalProducts";
import ContactInformation from "../../ContactInformation/ContactInformation";
import PaymentMethodSelector from "../../PaymentMethodSelector/PaymentMethodSelector";
import { StepAccordionCard } from "../StepAccordionCard/StepAccordionCard";
import { useTranslations } from "hooks/useTranslations";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsValid,
  setIsExpanded,
  goToStep,
  checkoutStepsSelector,
} from "redux/checkoutSteps";
import { setInTransition } from "redux/transition/transition.slice";
import TravelerInformation from "../../TravelerInformation/TravelerInformation";
import { selectedItinerarySelector } from "redux/selectedItinerary";
import { BrandedFares } from "../../BrandedFares/BrandedFares";

export const CheckoutMainContent = () => {
  const dispatch = useDispatch();

  const { breakpoints, transitions } = useTheme();
  const isTablet = useMediaQuery(breakpoints.down("md"));

  const {
    translations: {
      brandedFares,
      contactInformation,
      additionalProducts,
      paymentMethod,
      travelerInformation,
    },
  } = useTranslations();

  const checkoutSteps = useSelector(checkoutStepsSelector);
  const { includesBrandedFares, itineraries, indexOfRequestedItinerary } =
    useSelector(selectedItinerarySelector);

  const showBrandedFares = includesBrandedFares && itineraries.length > 1;

  const brandedFaresDelay = showBrandedFares ? 150 : 0;

  const handleExpansionEvent = (key: string, expanded: boolean) => {
    dispatch(setIsExpanded({ key: key, expanded: expanded }));
  };

  const setValidState = (key: string, isValid: boolean): void => {
    dispatch(setIsValid({ key: key, valid: isValid }));
  };

  const handleProceed = (index: number): void => {
    if (isTablet) {
      dispatch(setInTransition(true));

      setTimeout(() => {
        dispatch(goToStep(index));
        window.scrollTo(0, 0);
        dispatch(setInTransition(false));
      }, transitions.duration.standard);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        dispatch(goToStep(index));
      }, 100);
    }
  };
  return (
    <>
      {itineraries[indexOfRequestedItinerary] &&
        itineraries[indexOfRequestedItinerary].youthStudentMaxAge && (
          <CustomAnimation {...fadeInAccordionAnimations} delay={300}>
            <Alert
              variant="neutral"
              icon={false}
              sx={{
                textAlign: "center",
                justifyContent: "space-around",
                mb: 3,
              }}
            >
              <YouthStudentInfo
                itinerary={itineraries[indexOfRequestedItinerary]}
              />
            </Alert>
          </CustomAnimation>
        )}
      {showBrandedFares && (
        <CustomAnimation {...fadeInAccordionAnimations} delay={350}>
          <StepAccordionCard
            title={brandedFares.title}
            expanded={checkoutSteps.steps.brandedFares.expanded}
            disabled={checkoutSteps.steps.brandedFares.disabled}
            valid={checkoutSteps.steps.brandedFares.valid}
            accordionKey="brandedFares"
            handleExpansionEvent={handleExpansionEvent}
            sx={{
              "&:before": {
                position: "absolute",
                height: "100%",
                width: 74,
                content: "''",
                top: 75,
                left: 0,
                zIndex: 2,
                background:
                  "linear-gradient(90deg, rgba(255,255,255,1) 5%, rgba(255,255,255,0) 100%)",
              },
              "&:after": {
                position: "absolute",
                height: "100%",
                width: 74,
                content: "''",
                top: 75,
                right: 0,
                zIndex: 2,
                background:
                  "linear-gradient(270deg, rgba(255,255,255,1) 5%, rgba(255,255,255,0) 100%)",
                pointerEvents: "none",
              },
            }}
          >
            <BrandedFares
              handleProceed={() => handleProceed(1)}
              valid={checkoutSteps.steps.brandedFares.valid}
            />
          </StepAccordionCard>
        </CustomAnimation>
      )}
      <CustomAnimation
        {...fadeInAccordionAnimations}
        delay={brandedFaresDelay + 350}
      >
        <StepAccordionCard
          title={contactInformation.title}
          expanded={checkoutSteps.steps.contactInformation.expanded}
          disabled={checkoutSteps.steps.contactInformation.disabled}
          accordionKey="contactInformation"
          handleExpansionEvent={handleExpansionEvent}
          valid={checkoutSteps.steps.contactInformation.valid}
        >
          <ContactInformation
            validateHandler={(isValid: boolean) =>
              setValidState("contactInformation", isValid)
            }
            handleProceed={() => handleProceed(2)}
            initialValidState={checkoutSteps.steps.contactInformation.valid}
          />
        </StepAccordionCard>
      </CustomAnimation>
      <CustomAnimation
        {...fadeInAccordionAnimations}
        delay={brandedFaresDelay + 600}
      >
        <StepAccordionCard
          title={travelerInformation.title}
          expanded={checkoutSteps.steps.travelerInformation.expanded}
          disabled={checkoutSteps.steps.travelerInformation.disabled}
          accordionKey="travelerInformation"
          handleExpansionEvent={handleExpansionEvent}
          valid={checkoutSteps.steps.travelerInformation.valid}
        >
          <TravelerInformation
            validateHandler={(isValid: boolean) =>
              setValidState("travelerInformation", isValid)
            }
            handleProceed={() => handleProceed(3)}
            initialValidState={checkoutSteps.steps.travelerInformation.valid}
          />
        </StepAccordionCard>
      </CustomAnimation>
      <CustomAnimation
        {...fadeInAccordionAnimations}
        delay={brandedFaresDelay + 850}
      >
        <StepAccordionCard
          title={additionalProducts.title}
          expanded={checkoutSteps.steps.additionalProducts.expanded}
          disabled={checkoutSteps.steps.additionalProducts.disabled}
          accordionKey="additionalProducts"
          handleExpansionEvent={handleExpansionEvent}
          valid={checkoutSteps.steps.additionalProducts.valid}
        >
          <AdditionalProducts
            validateHandler={(isValid: boolean) =>
              setValidState("additionalProducts", isValid)
            }
            handleProceed={() => handleProceed(4)}
            initialValidState={checkoutSteps.steps.additionalProducts.valid}
          />
        </StepAccordionCard>
      </CustomAnimation>
      <CustomAnimation
        {...fadeInAccordionAnimations}
        delay={brandedFaresDelay + 1100}
      >
        <StepAccordionCard
          title={paymentMethod.title}
          expanded={checkoutSteps.steps.paymentMethod.expanded}
          disabled={checkoutSteps.steps.paymentMethod.disabled}
          accordionKey="paymentMethod"
          handleExpansionEvent={handleExpansionEvent}
          valid={checkoutSteps.steps.paymentMethod.valid}
        >
          <PaymentMethodSelector
            validateHandler={(isValid: boolean) =>
              setValidState("paymentMethod", isValid)
            }
          />
        </StepAccordionCard>
      </CustomAnimation>
    </>
  );
};
